import React, { useState, useEffect } from 'react';
import { ArrowDownIcon, LinkIcon } from '@heroicons/react/24/outline';
import { preferencesApi } from '../../../repositories/preferences';
import { useAppSelector } from '../../../hooks/redux';
import TermsOfServiceModal from '../../modal/TermsOfServiceModal';
import { userApi } from '../../../repositories/user';
import PrivacyPolicyModal from '../../modal/PrivacyPolicyModal';
import ToggleSwitch from '../../ui/ToggleSwitch';
import SettingsPanel from '../../ui/SettingsPanel';

interface Preferences {
  dataSharing: boolean;
  usageAnalytics: boolean;
  emailNotifications: boolean;
}

const Privacy: React.FC = () => {
  const [dataSharing, setDataSharing] = useState<boolean>(true);
  const [usageAnalytics, setUsageAnalytics] = useState<boolean>(true);
  const [emailNotifications, setEmailNotifications] = useState<boolean>(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);


  useEffect(() => {
    if (!user) return;
    const fetchPreferences = async () => {
      try {
        const { data } = await preferencesApi.getPreferences(user.id);
        setDataSharing(data.dataSharing);
        setUsageAnalytics(data.usageAnalytics);
        setEmailNotifications(data.emailNotifications);
      } catch (error: any) {
        console.error('Error fetching preferences:', error.response?.data?.error || error.message);
      }
    };
    fetchPreferences();
  }, [user]);

  const updatePreference = async (updates: Partial<Preferences>) => {
    if (!user) return;
    try {
      await preferencesApi.updatePreferences(user.id, updates);
    } catch (error: any) {
      console.error('Error updating preferences:', error.response?.data?.error || error.message);
    }
  };

  const handleToggle = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    currentValue: boolean,
    field: keyof Preferences
  ) => {
    const newValue = !currentValue;
    setter(newValue);
    updatePreference({ [field]: newValue });
  };

  const handleExportData = async () => {
    if (!user) return;
    setExportLoading(true);
    setExportSuccess(false);
    setExportError(null);

    try {
      if (typeof userApi.exportData !== "function") {
        setExportError("Export function not available.");
        setExportLoading(false);
        return;
      }

      const response = await userApi.exportData();

      // The backend returns 200 (with content-type text/csv) but a plain-text
      // body of "No pitch data found for export." when there is nothing to
      // export. Detect that by the body content itself — not the content-type —
      // so we never download a file whose contents are just that message.
      const rawText =
        typeof response.data === "string"
          ? response.data
          : response.data?.message ?? response.data?.error ?? "";
      const noData =
        !response.data || /no pitch data found/i.test(rawText);

      if (noData) {
        setExportError(
          rawText.trim() || "No pitch data found to export."
        );
        setExportLoading(false);
        return;
      }

      // Get filename from header or fallback
      const disposition = response.headers["content-disposition"];
      let filename = `cadence-pitch-data-${new Date().toISOString().slice(0, 10)}.csv`;
      if (disposition && disposition.includes("filename=")) {
        filename = disposition.split("filename=")[1].replace(/['"]/g, "").trim();
      }

      // Create blob from CSV string content
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);

      // Download file
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
    } catch (error: any) {
      console.error("Export error:", error);
      if (error.response?.status === 404) {
        setExportError("No pitch data found to export.");
      } else if (error.response?.data?.error) {
        setExportError(error.response.data.error);
      } else {
        setExportError("Network error. Please try again.");
      }
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportSuccess(false), 3000); // Hide success after 3s
    }
  };

  return (
    <>
      <SettingsPanel
        title="Privacy & Data Settings"
        description="Control how your data is used and stored"
        divided
      >

        {/* Data Preferences */}
        <div className="mb-4 sm:mb-6">
          <h3 className="mb-4 text-sm font-semibold text-black sm:text-base">Data Preferences</h3>
          <div className="space-y-4">
            {/* Data Sharing Toggle */}
            <div className="p-3 border rounded-md border-neutral-200 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-black sm:text-sm">Data sharing with connected DSPs</p>
                  <p className="text-xs text-neutral-600">Allow Cadence to share pitch data with streaming platforms</p>
                </div>
                <ToggleSwitch
                  checked={dataSharing}
                  onChange={() => handleToggle(setDataSharing, dataSharing, 'dataSharing')}
                  label="Data sharing with connected DSPs"
                />
              </div>
            </div>

            {/* Usage Analytics Toggle */}
            <div className="p-3 border rounded-md border-neutral-200 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-black sm:text-sm">Usage analytics opt-in</p>
                  <p className="text-xs text-neutral-600">Help us improve by sharing anonymous usage data</p>
                </div>
                <ToggleSwitch
                  checked={usageAnalytics}
                  onChange={() => handleToggle(setUsageAnalytics, usageAnalytics, 'usageAnalytics')}
                  label="Usage analytics opt-in"
                />
              </div>
            </div>

            {/* Email Notifications Toggle */}
            <div className="p-3 border rounded-md border-neutral-200 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-black sm:text-sm">Email notifications</p>
                  <p className="text-xs text-neutral-600">Receive updates about your pitches and account</p>
                </div>
                <ToggleSwitch
                  checked={emailNotifications}
                  onChange={() => handleToggle(setEmailNotifications, emailNotifications, 'emailNotifications')}
                  label="Email notifications"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Data & Legal */}
        <div className="mb-4 sm:mb-6">
          <h3 className="mb-4 text-sm font-semibold text-black sm:text-base">Data & Legal</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Export Data */}
            <button
              onClick={handleExportData}
              className="relative flex items-center p-3 transition-colors border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
              disabled={exportLoading}
            >
              <ArrowDownIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-black sm:text-sm">
                {exportLoading ? "Exporting..." : "Export Your Data"}
              </span>
              {exportLoading && (
                <span className="w-4 h-4 ml-2 border-2 rounded-full border-primary-700 border-t-transparent animate-spin"></span>
              )}
              {exportSuccess && (
                <span className="ml-2 text-xs font-semibold text-success-600">Downloaded!</span>
              )}
              {exportError && (
                <span className="ml-2 text-xs font-semibold text-danger-600">{exportError}</span>
              )}
            </button>

            {/* Privacy Policy */}
            {/* <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
              className="flex items-center p-3 transition-colors border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-black sm:text-sm">Privacy Policy</span>
            </a> */}
            <button
              onClick={() => setShowPrivacyModal(true)}
              className="relative flex items-center p-3 transition-colors border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-black sm:text-sm">Privacy Policy</span>
            </button>


            {/* Terms of Service */}
            <button
              onClick={() => setShowTermsModal(true)}
              className="flex items-center p-3 transition-colors border border-neutral-200 rounded-md sm:p-4 hover:bg-neutral-50 sm:col-span-2"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-black sm:text-sm">Terms of Service</span>
            </button>
          </div>
        </div>
      </SettingsPanel>

      {/* Modals */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onAccept={() => setShowTermsModal(false)}
        onDecline={() => setShowTermsModal(false)}
      />



      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

    </>
  );
};

export default Privacy;